import React, { useState, useEffect } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ProjectInterface } from "../../../../models/project.model";
import { updateProject } from "../../../../api/project";
import toastifyInstance from "../../../../utils/toastify";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { format, isValid, parseISO } from "date-fns";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../components/ui/form";

// Define the validation schema using Yup
const projectSchema = yup.object({
    name: yup.string().required("Project name is required"),
    description: yup.string(),
    startDate: yup.date().nullable().typeError("Please enter a valid date"),
    endDate: yup
        .date()
        .nullable()
        .typeError("Please enter a valid date")
        .min(yup.ref("startDate"), "End date cannot be before start date"),
    imageUrl: yup.string().nullable(),
    clientId: yup.string().nullable(),
    url: yup.string().nullable(),
    slack_channel_id: yup.string().nullable(),
    status: yup.boolean().nullable(),
    private: yup.boolean().nullable(),
}).required();

type FormValues = yup.InferType<typeof projectSchema>;

const UpdateProject: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const itemId = searchParams.get('id') || '';
    const queryClient = useQueryClient();
    const dataCurrent = queryClient
        .getQueryData<ProjectInterface[]>(["projects"])
        ?.find((item) => item._id === itemId);

    const [imageUrl, setImageUrl] = useState<string | null>(dataCurrent?.imageUrl || "");
    const [useFileUpload, setUseFileUpload] = useState(true);

    // Initialize react-hook-form
    const form = useForm<FormValues>({
        resolver: yupResolver(projectSchema),
        defaultValues: {
            name: dataCurrent?.name || "",
            description: dataCurrent?.description || "",
            startDate: dataCurrent?.startDate ? parseISO(dataCurrent.startDate.toString()) : null,
            endDate: dataCurrent?.endDate ? parseISO(dataCurrent.endDate.toString()) : null,
            imageUrl: dataCurrent?.imageUrl || "",
            clientId: dataCurrent?.clientId || "",
            url: dataCurrent?.url || "",
            slack_channel_id: dataCurrent?.slack_channel_id || "",
            status: dataCurrent?.status || false,
            private: dataCurrent?.private || false,
        },
    });

    // Redirect if project not found
    useEffect(() => {
        if (!dataCurrent || !itemId) {
            navigate("/projects");
        }
    }, [dataCurrent, itemId, navigate]);

    // Update form values when data changes
    useEffect(() => {
        if (dataCurrent) {
            form.reset({
                name: dataCurrent.name,
                description: dataCurrent.description || "",
                startDate: dataCurrent.startDate ? parseISO(dataCurrent.startDate.toString()) : null,
                endDate: dataCurrent.endDate ? parseISO(dataCurrent.endDate.toString()) : null,
                imageUrl: dataCurrent.imageUrl || "",
                clientId: dataCurrent.clientId || "",
                url: dataCurrent.url || "",
                slack_channel_id: dataCurrent.slack_channel_id || "",
                status: dataCurrent.status || false,
                private: dataCurrent.private || false,
            });
            setImageUrl(dataCurrent.imageUrl || "");
            setUseFileUpload(!(dataCurrent.imageUrl && dataCurrent.imageUrl.startsWith('http')));
        }
    }, [dataCurrent, form]);

    // Project update mutation
    const mutation = useMutation({
        mutationFn: (payload: ProjectInterface) => {
            return updateProject(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            toastifyInstance.success("Project updated successfully!");
            navigate("/projects");
        },
        onError: (error: any) => {
            toastifyInstance.error("Error updating project: " + error.message);
        },
    });

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                setImageUrl(result);
                form.setValue("imageUrl", result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleToggleImageInput = () => {
        setUseFileUpload((prev) => !prev);
        if (!useFileUpload) {
            // Switching to file upload
            setImageUrl(null);
        } else {
            // Switching to URL input
            const urlValue = form.getValues("imageUrl") || "";
            setImageUrl(urlValue);
        }
    };

    // Format dates for display
    const formatDateForInput = (date: Date | null): string => {
        if (!date || !isValid(date)) return "";
        return format(date, "yyyy-MM-dd");
    };

    const onSubmit = (formData: FormValues) => {
        if (dataCurrent) {
            const finalImageUrl = useFileUpload ? imageUrl : formData.imageUrl;

            mutation.mutate({
                ...dataCurrent,
                ...formData,
                imageUrl: finalImageUrl || dataCurrent.imageUrl,
                _id: dataCurrent._id,
                startDate: formData.startDate || undefined,
                endDate: formData.endDate || undefined,
                clientId: formData.clientId || undefined,
                url: formData.url || undefined,
                slack_channel_id: formData.slack_channel_id || undefined,
                status: formData.status === false ? false : true,
                private: formData.private === false ? false : true,
            });
        }
    };

    if (!dataCurrent) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Card className="w-full max-w-md">
                    <CardHeader className="bg-destructive text-destructive-foreground">
                        <CardTitle>Project Not Found</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <p>The project you are looking for does not exist or has been deleted.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="bg-muted min-h-screen py-8 px-4">
            <Card className="max-w-3xl mx-auto">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                    <CardTitle>Update Project</CardTitle>
                    <CardDescription className="text-blue-100">
                        Edit your project details below
                    </CardDescription>
                </CardHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <CardContent className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="col-span-full">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Project Name</FormLabel>
                                                <FormControl>
                                                    <Input {...field} placeholder="Enter project name" />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="col-span-full">
                                    <FormField
                                        control={form.control}
                                        name="description"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Description</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        {...field}
                                                        placeholder="Describe your project"
                                                        rows={4}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <FormField
                                    control={form.control}
                                    name="slack_channel_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Slack Channel ID</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter Slack Channel ID" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="url"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Link</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter Link" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="clientId"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Client</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter Client" {...field} value={field.value || ""} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="grid grid-cols-2 gap-6">
                                    <FormField
                                        control={form.control}
                                        name="status"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-col">
                                                <FormLabel>Status</FormLabel>
                                                <FormControl>
                                                    <Switch checked={field.value || false} onCheckedChange={(checked) => {
                                                        form.setValue("status", checked);
                                                    }} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="private"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-col">
                                                <FormLabel>Private</FormLabel>
                                                <FormControl>
                                                    <Switch checked={field.value || false} onCheckedChange={(checked) => {
                                                        form.setValue("private", checked);
                                                    }} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div>
                                    <FormField
                                        control={form.control}
                                        name="startDate"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Start Date</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="date"
                                                        value={formatDateForInput(field.value as Date)}
                                                        onChange={(e) => {
                                                            const date = e.target.value ? new Date(e.target.value) : null;
                                                            field.onChange(date);
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div>
                                    <FormField
                                        control={form.control}
                                        name="endDate"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>End Date</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="date"
                                                        value={formatDateForInput(field.value as Date)}
                                                        onChange={(e) => {
                                                            const date = e.target.value ? new Date(e.target.value) : null;
                                                            field.onChange(date);
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="col-span-full">
                                    <div className="flex items-center justify-between mb-2">
                                        <Label>Project Image</Label>
                                        <div className="flex items-center space-x-2">
                                            <span className={!useFileUpload ? "text-primary text-xs" : "text-muted-foreground text-xs"}>
                                                URL
                                            </span>
                                            <Switch
                                                checked={useFileUpload}
                                                onCheckedChange={handleToggleImageInput}
                                            />
                                            <span className={useFileUpload ? "text-primary text-xs" : "text-muted-foreground text-xs"}>
                                                Upload
                                            </span>
                                        </div>
                                    </div>

                                    {useFileUpload ? (
                                        <div className="mt-1">
                                            <Input
                                                type="file"
                                                id="imageFile"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                            />
                                        </div>
                                    ) : (
                                        <div className="mt-1">
                                            <FormField
                                                control={form.control}
                                                name="imageUrl"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                type="url"
                                                                placeholder="Enter image URL (https://...)"
                                                                {...field}
                                                                value={field.value || ""}
                                                                onChange={(e) => {
                                                                    field.onChange(e);
                                                                    setImageUrl(e.target.value);
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    )}

                                    {imageUrl && (
                                        <div className="mt-3">
                                            <span className="text-sm text-muted-foreground">Preview:</span>
                                            <div className="mt-1">
                                                <img
                                                    src={imageUrl}
                                                    alt="Project Preview"
                                                    className="w-32 h-32 object-cover rounded-md border shadow-sm"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = "https://via.placeholder.com/150?text=Image+Error";
                                                        target.onerror = null;
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => window.history.back()}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={mutation.isPending}
                                >
                                    {mutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            <span>Updating...</span>
                                        </>
                                    ) : (
                                        "Update Project"
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </form>
                </Form>
            </Card>
        </div>
    );
};

export default UpdateProject;